import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCrawl,
  addCrawl,
  updateCrawl,
  updateUserPreference,
  deleteCrawl,
  activateDeactivateCrawl,
  fetchCrawlTableColumnNames,
} from "../../../slices/thunks";
import { getAuthUser } from "../../../helpers/utils";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import CrawlForm from "../../../Components/Common/AddCrawlForm/AddCrawlForm";

const labels = [
  {
    id: 1,
    labelName: "Link",
    fieldName: "text-box",
    name: "Link",
    value: "Link",
    required: true,
    errorMessage: "LinkRequired",
  },
  {
    id: 2,
    labelName: "Schedule",
    fieldName: "text-box",
    name: "Schedule",
    value: "Schedule",
    required: true,
    errorMessage: "ScheduleRequired",
  }
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function DocumentCrawl() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user } = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const {Crawl, loading, tableColumnNames } = useSelector(
    (state) => ({
      tableColumnNames: state.Crawl.tableColumnNames,
      loading: state.Crawl.loading,
      Crawl: state.Crawl.crawl,
    })
  );

  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (user, value) => {
    setSelectedRow(user);
    setIsAddOrEdit(value);
    setShow(true);
  };
    

  const onSubmitHandler = (values, { resetForm }) => {
    // Prepare the payload
    const crawlObj = {
      Link: values.Link.trim(),
      Schedule: values.Schedule.trim(),
    };

    if (isAddOrEdit === "isEdit" && selectedRow) {
      crawlObj["ID"] = selectedRow.ID; 
    }

    if (isAddOrEdit === "isAdd") {
      dispatch(addCrawl(crawlObj));
    } else {
      dispatch(updateCrawl(crawlObj));
    }
    resetForm();
    setShow(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchCrawlTableColumnNames({
          module: "HUMAN_CATEGORIES",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteCrawl({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (item, e) => {
    dispatch(activateDeactivateCrawl({ ID: item.ID }))
  };

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 16);
  })();


  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchCrawlTableColumnNames({
            module: "CRAWL",
          })
        ).unwrap();
        await dispatch(fetchCrawl());
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Crawl")} />
          </Col>
        </Row>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => handleClassesClicks("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                     <CommonDataTable
                      data={Crawl}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={CrawlForm}
        title={isAddOrEdit === "isAdd" ? t("AddNewCrawl") : t("EditCrawl")}
        size="lg"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />

    </div>
  );
}

export default DocumentCrawl;
